import { UnknownAction } from '@reduxjs/toolkit'
import { createReducer } from '@reduxjs/toolkit/react'
import {
  AssistantItems,
  BeneficiaryInsightsAssistemItems,
  Traceable,
} from '@repo/shared/dtos/assistant'
import { TimeType } from '@repo/shared/dtos/generic'
import { ChatMessageSender } from '@repo/shared/dtos/message'
import { AsyncData, DataStatus, findLastChatSessionId } from '@repo/ui/utils'

import { storeActions } from '@/store/actions'

export type AnalyticsChatItem = BeneficiaryInsightsAssistemItems[0] | AssistantItems['message']

export type ChatMessage = {
  content: string
  id: string
  sender: ChatMessageSender
  sentAt: TimeType
} & Traceable

export type ChatSession = {
  closedAt?: TimeType
  id: string
  startedAt: TimeType
}

export type CurrentChatSession = {
  id: string
  messages: ChatMessage[]
}

export type ChatState = {
  currentSession: AsyncData<CurrentChatSession>
  sessions: AsyncData<ChatSession[]>
}

export const chatInitialState: ChatState = {
  currentSession: DataStatus.Uninitialized,
  sessions: DataStatus.Uninitialized,
}

export const selectChatSessionId = (state: { chat: ChatState }) => {
  if (!DataStatus.isFulfilled(state.chat.sessions) || state.chat.sessions.length === 0)
    return undefined
  if (DataStatus.isFulfilled(state.chat.currentSession)) {
    return state.chat.currentSession.id
  }
  return findLastChatSessionId(state.chat.sessions)
}

export const chatReducer: ((state: ChatState | undefined, action: UnknownAction) => ChatState) & {
  getInitialState: () => ChatState
} = createReducer(chatInitialState, (builder) => {
  builder.addCase(storeActions.cleanUpOrganization, () => {
    return chatInitialState
  })

  builder
    .addCase(
      storeActions.beneficiary.initBeneficiary.pending,
      (
        state,
        {
          meta: {
            arg: { isSameBeneficiary },
          },
        },
      ) => {
        if (isSameBeneficiary) return
        state.currentSession = DataStatus.Uninitialized
        state.sessions = DataStatus.Loading
      },
    )
    .addCase(storeActions.beneficiary.initBeneficiary.rejected, (state) => {
      state.currentSession = DataStatus.Uninitialized
      state.sessions = DataStatus.Error
    })
    .addCase(
      storeActions.beneficiary.initBeneficiary.fulfilled,
      (state, { payload: { chatSessions } }) => {
        state.currentSession = DataStatus.Uninitialized
        state.sessions = chatSessions
      },
    )

  builder
    .addCase(storeActions.beneficiary.chatSession.initChatSession.pending, (state) => {
      if (DataStatus.isFulfilled(state.sessions)) state.currentSession = DataStatus.Loading
    })
    .addCase(storeActions.beneficiary.chatSession.initChatSession.rejected, (state) => {
      state.currentSession = DataStatus.Error
    })
    .addCase(
      storeActions.beneficiary.chatSession.initChatSession.fulfilled,
      (
        state,
        {
          payload: { chatMessages },
          meta: {
            arg: { chatSessionId },
          },
        },
      ) => {
        state.currentSession = {
          id: chatSessionId,
          messages: chatMessages,
        }
      },
    )

  builder.addCase(
    storeActions.beneficiary.chatSession.create.fulfilled,
    (state, { payload: { chatSession } }) => {
      state.sessions = DataStatus.isFulfilled(state.sessions)
        ? [...state.sessions, chatSession]
        : [chatSession]
    },
  )
})
